import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { 
  Image, Download, Trash2, Share2, Copy, Check, 
  Instagram, Facebook, Twitter, Sparkles, BarChart3,
  Palette, Layout, RefreshCw, Eye, MousePointerClick, Send
} from 'lucide-react';

export default function MarketingTools() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState({ templates: [], color_schemes: [] });
  const [myAds, setMyAds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('generate');
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedAdForShare, setSelectedAdForShare] = useState(null);
  const [shareCaption, setShareCaption] = useState('');
  const [selectedShareAccounts, setSelectedShareAccounts] = useState([]);
  const [posting, setPosting] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    content_type: 'custom',
    content_id: '',
    template: 'instagram_square',
    color_scheme: 'cosmic_dark',
    headline: '',
    subtitle: '',
    quote: '',
    include_logo: true,
    include_website: true
  });
  
  const [generatedAd, setGeneratedAd] = useState(null);
  const [contentOptions, setContentOptions] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (formData.content_type !== 'custom') {
      loadContentOptions(formData.content_type);
    }
  }, [formData.content_type]);

  const loadData = async () => {
    try {
      const [templatesRes, adsRes, analyticsRes, accountsRes] = await Promise.all([
        axios.get(`${API}/marketing/templates`),
        axios.get(`${API}/marketing/my-ads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/marketing/analytics`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/social/accounts`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { accounts: [] } }))
      ]);
      setTemplates(templatesRes.data);
      setMyAds(adsRes.data);
      setAnalytics(analyticsRes.data);
      setConnectedAccounts(accountsRes.data?.accounts || []);
    } catch (error) {
      console.error('Failed to load marketing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadContentOptions = async (type) => {
    try {
      let endpoint = '';
      if (type === 'meditation') endpoint = '/meditations';
      else if (type === 'affirmation') endpoint = '/affirmations';
      else if (type === 'article') endpoint = '/articles';
      
      if (endpoint) {
        const response = await axios.get(`${API}${endpoint}`);
        setContentOptions(response.data.slice(0, 20));
      }
    } catch (error) {
      console.error('Failed to load content options:', error);
    }
  };

  const generateAd = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setGeneratedAd(null);
    
    try {
      const response = await axios.post(
        `${API}/marketing/generate-ad`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setGeneratedAd(response.data);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate ad');
    } finally {
      setGenerating(false);
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('Delete this ad?')) return;
    try {
      await axios.delete(`${API}/marketing/ads/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadData();
    } catch (error) {
      alert('Failed to delete ad');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openShareModal = (ad) => {
    setSelectedAdForShare(ad);
    setShareCaption(ad.headline + (ad.subtitle ? '\n\n' + ad.subtitle : '') + '\n\n#TheAuraSpace #Mindfulness #Wellness');
    setSelectedShareAccounts([]);
    setShowShareModal(true);
  };

  const postToSocialMedia = async () => {
    if (selectedShareAccounts.length === 0) {
      alert('Please select at least one account');
      return;
    }
    
    setPosting(true);
    try {
      // Create a scheduled post with the ad image
      const imageUrl = `${window.location.origin}${selectedAdForShare.image_url}`;
      
      const response = await axios.post(
        `${API}/social/posts`,
        {
          content: shareCaption,
          image_url: imageUrl,
          ad_id: selectedAdForShare.id,
          account_ids: selectedShareAccounts
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Now publish immediately
      const postId = response.data.id;
      const publishResponse = await axios.post(
        `${API}/social/posts/${postId}/publish`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const results = publishResponse.data.results || {};
      const successful = Object.values(results).filter(r => r.success).length;
      const failed = Object.values(results).filter(r => !r.success).length;
      
      if (successful > 0) {
        alert(`Successfully posted to ${successful} account(s)!${failed > 0 ? ` (${failed} failed)` : ''}`);
      } else if (failed > 0) {
        const errors = Object.values(results).map(r => r.message).join('\n');
        alert(`Failed to post:\n${errors}`);
      }
      
      setShowShareModal(false);
      setSelectedAdForShare(null);
    } catch (error) {
      console.error('Failed to post to social media:', error);
      alert(error.response?.data?.detail || 'Failed to post to social media');
    } finally {
      setPosting(false);
    }
  };

  const getTemplateIcon = (templateId) => {
    if (templateId.includes('instagram')) return Instagram;
    if (templateId.includes('facebook')) return Facebook;
    if (templateId.includes('twitter')) return Twitter;
    return Image;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: currentColors.primary }} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="marketing-tools">
      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center gap-3 mb-2">
          <Image className="w-8 h-8" style={{ color: currentColors.primary }} />
          <h1 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
            Marketing Tools
          </h1>
        </div>
        <p style={{ color: currentColors.textMuted }}>
          Create beautiful ad images and shareable content for social media
        </p>
      </div>

      {/* Analytics Summary */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Ads Generated', value: analytics.ads?.total_generated || 0, icon: Image },
            { label: 'Downloads', value: analytics.ads?.total_downloads || 0, icon: Download },
            { label: 'Link Clicks', value: analytics.shares?.total_clicks || 0, icon: MousePointerClick },
            { label: 'Shares', value: analytics.shares?.total_shares || 0, icon: Share2 }
          ].map((stat, idx) => (
            <div 
              key={idx} 
              className="glass-card p-4 text-center"
            >
              <stat.icon className="w-6 h-6 mx-auto mb-2" style={{ color: currentColors.secondary }} />
              <p className="text-2xl font-bold" style={{ color: currentColors.primary }}>{stat.value}</p>
              <p className="text-xs" style={{ color: currentColors.textMuted }}>{stat.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { id: 'generate', label: 'Generate Ad', icon: Sparkles },
          { id: 'library', label: 'My Ads', icon: Image },
          { id: 'analytics', label: 'Analytics', icon: BarChart3 }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
            style={{
              backgroundColor: activeTab === tab.id ? `${currentColors.primary}20` : 'transparent',
              color: activeTab === tab.id ? currentColors.primary : currentColors.textMuted,
              border: `1px solid ${activeTab === tab.id ? currentColors.primary : currentColors.border}`
            }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Generate Tab */}
      {activeTab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Form */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
              Create New Ad
            </h2>
            <form onSubmit={generateAd} className="space-y-4">
              {/* Content Source */}
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Content Source
                </label>
                <select
                  value={formData.content_type}
                  onChange={(e) => setFormData({ ...formData, content_type: e.target.value, content_id: '' })}
                  className="cosmic-input w-full"
                >
                  <option value="custom">Custom Text</option>
                  <option value="meditation">From Meditation</option>
                  <option value="affirmation">From Affirmation</option>
                  <option value="article">From Article</option>
                </select>
              </div>

              {/* Content Selection */}
              {formData.content_type !== 'custom' && contentOptions.length > 0 && (
                <div>
                  <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                    Select Content
                  </label>
                  <select
                    value={formData.content_id}
                    onChange={(e) => setFormData({ ...formData, content_id: e.target.value })}
                    className="cosmic-input w-full"
                  >
                    <option value="">Choose...</option>
                    {contentOptions.map(item => (
                      <option key={item.id} value={item.id}>
                        {item.title || item.text?.substring(0, 50)}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Template */}
              <div>
                <label className="block text-sm mb-2" style={{ color: currentColors.textMuted }}>
                  Template
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {templates.templates?.map(template => {
                    const Icon = getTemplateIcon(template.id);
                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, template: template.id })}
                        className="p-3 rounded-lg text-left transition-all"
                        style={{
                          backgroundColor: formData.template === template.id 
                            ? `${currentColors.primary}20` 
                            : `${currentColors.textMuted}10`,
                          border: `1px solid ${formData.template === template.id ? currentColors.primary : 'transparent'}`
                        }}
                      >
                        <Icon className="w-5 h-5 mb-1" style={{ color: formData.template === template.id ? currentColors.primary : currentColors.textMuted }} />
                        <p className="text-sm font-medium" style={{ color: currentColors.text }}>{template.name}</p>
                        <p className="text-xs" style={{ color: currentColors.textMuted }}>{template.size}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Color Scheme */}
              <div>
                <label className="block text-sm mb-2" style={{ color: currentColors.textMuted }}>
                  Color Scheme
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {templates.color_schemes?.map(scheme => (
                    <button
                      key={scheme.id}
                      type="button"
                      onClick={() => setFormData({ ...formData, color_scheme: scheme.id })}
                      className="p-3 rounded-lg text-left transition-all flex items-center gap-2"
                      style={{
                        backgroundColor: formData.color_scheme === scheme.id 
                          ? `${currentColors.primary}20` 
                          : `${currentColors.textMuted}10`,
                        border: `1px solid ${formData.color_scheme === scheme.id ? currentColors.primary : 'transparent'}`
                      }}
                    >
                      <Palette className="w-4 h-4" style={{ color: formData.color_scheme === scheme.id ? currentColors.primary : currentColors.textMuted }} />
                      <div>
                        <p className="text-sm font-medium" style={{ color: currentColors.text }}>{scheme.name}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Custom Text Fields */}
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Headline {formData.content_type === 'custom' && '*'}
                </label>
                <input
                  type="text"
                  value={formData.headline}
                  onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
                  className="cosmic-input w-full"
                  placeholder="e.g., Find Your Inner Peace"
                  required={formData.content_type === 'custom'}
                />
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Subtitle
                </label>
                <input
                  type="text"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  className="cosmic-input w-full"
                  placeholder="e.g., Transform your life with daily meditation"
                />
              </div>

              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Quote (optional)
                </label>
                <textarea
                  value={formData.quote}
                  onChange={(e) => setFormData({ ...formData, quote: e.target.value })}
                  className="cosmic-input w-full"
                  rows={2}
                  placeholder="Add an inspiring quote..."
                />
              </div>

              {/* Options */}
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.include_logo}
                    onChange={(e) => setFormData({ ...formData, include_logo: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm" style={{ color: currentColors.text }}>Include branding</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.include_website}
                    onChange={(e) => setFormData({ ...formData, include_website: e.target.checked })}
                    className="w-4 h-4 rounded"
                  />
                  <span className="text-sm" style={{ color: currentColors.text }}>Include website</span>
                </label>
              </div>

              <button
                type="submit"
                disabled={generating}
                className="w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                style={{ backgroundColor: currentColors.primary, color: '#fff' }}
              >
                {generating ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Ad
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Preview */}
          <div className="glass-card p-6">
            <h2 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
              Preview
            </h2>
            {generatedAd ? (
              <div className="space-y-4">
                <div className="rounded-lg overflow-hidden" style={{ backgroundColor: `${currentColors.textMuted}10` }}>
                  <img 
                    src={`${API.replace('/api', '')}${generatedAd.image_url}`} 
                    alt="Generated ad"
                    className="w-full h-auto"
                  />
                </div>
                <div className="flex gap-2">
                  <a
                    href={`${API.replace('/api', '')}${generatedAd.download_url}`}
                    download
                    className="flex-1 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-all"
                    style={{ backgroundColor: currentColors.primary, color: '#fff' }}
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </a>
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}${generatedAd.image_url}`)}
                    className="px-4 py-2 rounded-lg transition-all"
                    style={{ 
                      backgroundColor: `${currentColors.secondary}20`,
                      color: currentColors.secondary
                    }}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
                {/* Post to Social Media */}
                <button
                  onClick={() => openShareModal(generatedAd)}
                  className="w-full py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-all"
                  style={{ 
                    backgroundColor: `${currentColors.secondary}20`,
                    color: currentColors.secondary,
                    border: `1px solid ${currentColors.secondary}40`
                  }}
                >
                  <Share2 className="w-5 h-5" />
                  Post to Social Media
                </button>
                {connectedAccounts.length === 0 && (
                  <p className="text-xs text-center" style={{ color: currentColors.textMuted }}>
                    <button 
                      onClick={() => navigate('/admin/social')}
                      className="underline hover:no-underline"
                      style={{ color: currentColors.primary }}
                    >
                      Connect social accounts
                    </button> to post directly
                  </p>
                )}
              </div>
            ) : (
              <div 
                className="h-64 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${currentColors.textMuted}10` }}
              >
                <div className="text-center">
                  <Image className="w-12 h-12 mx-auto mb-2" style={{ color: `${currentColors.primary}30` }} />
                  <p style={{ color: currentColors.textMuted }}>
                    Your generated ad will appear here
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Library Tab */}
      {activeTab === 'library' && (
        <div className="glass-card p-6">
          <h2 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
            My Generated Ads
          </h2>
          {myAds.length === 0 ? (
            <div className="text-center py-12">
              <Image className="w-16 h-16 mx-auto mb-4" style={{ color: `${currentColors.primary}30` }} />
              <p style={{ color: currentColors.textMuted }}>
                No ads generated yet. Create your first ad!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {myAds.map(ad => (
                <div key={ad.id} className="group relative">
                  <div className="rounded-lg overflow-hidden" style={{ backgroundColor: `${currentColors.textMuted}10` }}>
                    <img 
                      src={`${API.replace('/api', '')}${ad.image_url}`} 
                      alt={ad.headline}
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all rounded-lg flex items-center justify-center gap-2">
                    <a
                      href={`${API.replace('/api', '')}${ad.image_url}`}
                      download
                      className="p-2 rounded-full bg-white/20 hover:bg-white/30"
                      title="Download"
                    >
                      <Download className="w-4 h-4 text-white" />
                    </a>
                    <button
                      onClick={() => openShareModal(ad)}
                      className="p-2 rounded-full bg-blue-500/50 hover:bg-blue-500/70"
                      title="Post to Social Media"
                    >
                      <Share2 className="w-4 h-4 text-white" />
                    </button>
                    <button
                      onClick={() => deleteAd(ad.id)}
                      className="p-2 rounded-full bg-red-500/50 hover:bg-red-500/70"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <p className="text-xs mt-2 truncate" style={{ color: currentColors.textMuted }}>
                    {ad.headline}
                  </p>
                  <p className="text-xs" style={{ color: currentColors.textMuted }}>
                    {ad.downloads || 0} downloads
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="glass-card p-6">
              <h3 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
                Ad Performance
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span style={{ color: currentColors.textMuted }}>Total Ads Generated</span>
                  <span className="font-bold" style={{ color: currentColors.primary }}>
                    {analytics.ads?.total_generated || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: currentColors.textMuted }}>Total Downloads</span>
                  <span className="font-bold" style={{ color: currentColors.primary }}>
                    {analytics.ads?.total_downloads || 0}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="glass-card p-6">
              <h3 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
                Share Performance
              </h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span style={{ color: currentColors.textMuted }}>Shareable Links Created</span>
                  <span className="font-bold" style={{ color: currentColors.primary }}>
                    {analytics.shares?.total_links || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: currentColors.textMuted }}>Total Link Clicks</span>
                  <span className="font-bold" style={{ color: currentColors.primary }}>
                    {analytics.shares?.total_clicks || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span style={{ color: currentColors.textMuted }}>Social Shares</span>
                  <span className="font-bold" style={{ color: currentColors.primary }}>
                    {analytics.shares?.total_shares || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {analytics.recent_ads?.length > 0 && (
            <div className="glass-card p-6">
              <h3 className="text-lg font-cinzel mb-4" style={{ color: currentColors.text }}>
                Recent Ads
              </h3>
              <div className="grid grid-cols-5 gap-4">
                {analytics.recent_ads.map(ad => (
                  <div key={ad.id} className="rounded-lg overflow-hidden">
                    <img 
                      src={`${API.replace('/api', '')}${ad.image_url}`}
                      alt={ad.headline}
                      className="w-full h-auto"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Share to Social Media Modal */}
      {showShareModal && selectedAdForShare && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="w-full max-w-xl rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
            style={{ backgroundColor: currentColors.cardBg }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-cinzel" style={{ color: currentColors.text }}>
                Post to Social Media
              </h3>
              <button 
                onClick={() => setShowShareModal(false)}
                className="p-1 rounded-lg hover:bg-white/10"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Ad Preview */}
            <div className="mb-4 rounded-lg overflow-hidden" style={{ backgroundColor: `${currentColors.textMuted}10` }}>
              <img 
                src={`${API.replace('/api', '')}${selectedAdForShare.image_url}`}
                alt={selectedAdForShare.headline}
                className="w-full h-auto max-h-48 object-contain"
              />
            </div>

            {/* Caption */}
            <div className="mb-4">
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                Caption
              </label>
              <textarea
                value={shareCaption}
                onChange={(e) => setShareCaption(e.target.value)}
                rows={4}
                className="w-full cosmic-input resize-none"
                placeholder="Write your caption..."
              />
            </div>

            {/* Select Accounts */}
            <div className="mb-6">
              <label className="block text-sm mb-2" style={{ color: currentColors.textMuted }}>
                Select Accounts to Post To
              </label>
              {connectedAccounts.length === 0 ? (
                <div 
                  className="p-4 rounded-xl text-center"
                  style={{ backgroundColor: `${currentColors.primary}10`, border: `1px solid ${currentColors.primary}30` }}
                >
                  <p className="text-sm mb-2" style={{ color: currentColors.text }}>
                    No social accounts connected
                  </p>
                  <button
                    onClick={() => { setShowShareModal(false); navigate('/admin/social'); }}
                    className="text-sm underline"
                    style={{ color: currentColors.primary }}
                  >
                    Connect accounts in Social Media Manager
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  {connectedAccounts.map(account => (
                    <label 
                      key={account.id}
                      className="flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all"
                      style={{ 
                        backgroundColor: selectedShareAccounts.includes(account.id) ? `${currentColors.primary}20` : `${currentColors.background}50`,
                        border: `1px solid ${selectedShareAccounts.includes(account.id) ? currentColors.primary : currentColors.border}`
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedShareAccounts.includes(account.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedShareAccounts([...selectedShareAccounts, account.id]);
                          } else {
                            setSelectedShareAccounts(selectedShareAccounts.filter(id => id !== account.id));
                          }
                        }}
                        className="w-4 h-4 rounded"
                      />
                      {account.platform === 'facebook' && <Facebook className="w-5 h-5" style={{ color: '#1877F2' }} />}
                      {account.platform === 'instagram' && <Instagram className="w-5 h-5" style={{ color: '#E4405F' }} />}
                      {account.platform === 'twitter' && <Twitter className="w-5 h-5" style={{ color: '#1DA1F2' }} />}
                      <div className="flex-1">
                        <p className="font-medium text-sm" style={{ color: currentColors.text }}>
                          {account.platform_name || account.platform_username}
                        </p>
                        <p className="text-xs" style={{ color: currentColors.textMuted }}>
                          {account.platform}
                        </p>
                      </div>
                      {account.is_active ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">Active</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">Demo</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowShareModal(false)}
                className="flex-1 py-2 rounded-lg transition-all"
                style={{ 
                  backgroundColor: 'transparent',
                  border: `1px solid ${currentColors.border}`,
                  color: currentColors.text
                }}
              >
                Cancel
              </button>
              <button
                onClick={postToSocialMedia}
                disabled={posting || selectedShareAccounts.length === 0}
                className="flex-1 py-2 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: currentColors.primary, color: '#fff' }}
              >
                {posting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Posting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Post Now
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
